import { Icon, Modal } from '@teamsnap/teamsnap-ui';
import BasicSearchFilter from 'components/common/table/basic-search';
import { APP_URL } from 'core/constants';
import { chunk } from 'lodash';
import * as React from 'react';
import OTHER_SPORTS from './other-sports';
import './sports-cards.scss';

type Props = {
  selectedSport: number | null;
  setSelectedSport: (x: number) => any;
};

type CardProps = {
  title: string;
  selected: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  children: React.ReactNode;
};

type OtherSportsProps = {
  show: boolean;
  close: () => any;
  selectSport: (x: { id: number; title: string }) => any;
};

const DEFAULT_SPORTS = [
  { id: 5, icon: 'baseball.svg', title: 'Baseball' },
  { id: 1, icon: 'basketball.svg', title: 'Basketball' },
  { id: 7, icon: 'football.svg', title: 'Football' },
  { id: 16, icon: 'hockey.svg', title: 'Ice Hockey' },
  { id: 10, icon: 'lacrosse.svg', title: 'Lacrosse' },
  { id: 2, icon: 'soccer.svg', title: 'Soccer' },
  { id: 4, icon: 'baseball.svg', title: 'Softball' },
  { id: 6, icon: 'volleyball.svg', title: 'Volleyball' },
];

const Card = ({ children, title, selected, onClick }: CardProps) => (
  <div
    role="none"
    onClick={onClick}
    className={`sport-card ${selected ? 'selected' : ''}`}
  >
    {children}
    <p className="card-title">{title}</p>
  </div>
);

const OtherSportsFlyout = ({ show, close, selectSport }: OtherSportsProps) => {
  const [searchParam, setSearchParam] = React.useState('');

  return (
    <Modal
      heading="Select a Sport"
      style={{ minWidth: '0', width: '300px' }}
      show={show}
      showClose
      allowOverlayClose
      closeFn={close}
    >
      <div className="other-sports-wrapper">
        <BasicSearchFilter
          searchPlaceholder="Search"
          searchValue={searchParam}
          searchFunction={({ searchTerm }) =>
            setSearchParam(searchTerm.trim().toLowerCase())
          }
        />

        {OTHER_SPORTS.filter((sport) =>
          sport.title.toLowerCase().includes(searchParam)
        ).map((sport) => (
          <li
            key={sport.id}
            role="none"
            className="program-guide-sport"
            onClick={() => {
              selectSport(sport);
              close();
            }}
          >
            {sport.title}
          </li>
        ))}
      </div>
    </Modal>
  );
};

const getInitialLastCard = (selectedSport: number) => {
  if (!selectedSport || DEFAULT_SPORTS.some((x) => x.id === selectedSport)) {
    return { id: 0, title: 'Other' };
  }
  return OTHER_SPORTS.find((x) => x.id === selectedSport);
};

const SportsCards = ({ selectedSport, setSelectedSport }: Props) => {
  const [showOtherSportsFlyout, setShowOtherSportsFlyout] =
    React.useState(false);
  const [lastCard, setLastCard] = React.useState(
    getInitialLastCard(selectedSport)
  );

  const LastCard = (
    <Card
      title={lastCard.title}
      selected={lastCard.title !== 'Other' && lastCard.id === selectedSport}
      onClick={() => setShowOtherSportsFlyout(true)}
    >
      {lastCard.title === 'Other' ? (
        <Icon className="Icon" mods="sport-icon" name="plus" />
      ) : (
        <img
          className="sport-icon"
          alt={lastCard.title}
          src={`${APP_URL}/images/workspaces.svg`}
        />
      )}
    </Card>
  );

  return (
    <section>
      <OtherSportsFlyout
        show={showOtherSportsFlyout}
        close={() => setShowOtherSportsFlyout(false)}
        selectSport={(sport) => {
          setSelectedSport(sport.id);
          setLastCard(sport);
        }}
      />

      {chunk(DEFAULT_SPORTS, 3).map((row, index) => (
        <div className="sports-row" key={index}>
          {row.map((sport) => (
            <Card
              key={sport.id}
              title={sport.title}
              selected={selectedSport === sport.id}
              onClick={() => setSelectedSport(sport.id)}
            >
              <img
                className="sport-icon"
                alt={sport.title}
                src={`${APP_URL}/images/${sport.icon}`}
              />
            </Card>
          ))}

          {row.length < 3 && LastCard}
        </div>
      ))}
    </section>
  );
};

export default SportsCards;
